import * as React from 'react'
import { Text, View } from 'react-native'
import styled from '@theme/styled-components'

import TextInput from '@components/textInput/TextInput'

import useI18n from '@i18n/useI18n'
import useReducer from '@store/useReducer'
import * as UserStore from '@store/user/user'

import { FormikHelpers, useFormik } from 'formik'
import useDebounce from '@utils/useDebounce'

import { IncidentFormScheme } from '../scheme'
import { formHasUser } from '../utils'

interface Values {
  nomContact: string
  prenomContact: string
  emailContact: string
  telephoneContact: string
}

const UserDetail = ({ form, onForm }: IncidentFormProps) => {
  const [currentName, name, setName] = useDebounce(form.nomContact)
  const [currentFirstName, firstname, setFirstName] = useDebounce(form.prenomContact)
  const [currentEmail, email, setEmail] = useDebounce(form.emailContact)
  const [currentPhone, phone, setPhone] = useDebounce(form.telephoneContact)

  const init: Values = React.useMemo(
    () => ({
      nomContact: form.nomContact,
      prenomContact: form.prenomContact,
      emailContact: form.emailContact,
      telephoneContact: form.telephoneContact,
    }),
    [form.nomContact, form.prenomContact, form.emailContact, form.telephoneContact]
  )

  const i18n = useI18n()
  const user = useReducer(UserStore.store, (s) => s.user)

  const onSubmit = (vals: Values, { setSubmitting }: FormikHelpers<Values>) => {
    onForm(vals).finally(() => setSubmitting(false))
  }

  const { values, setFieldValue, handleSubmit, dirty, errors, touched } = useFormik<Values>({
    enableReinitialize: true,
    initialValues: init,
    onSubmit,
    validationSchema: IncidentFormScheme.USER_DETAIL,
  })

  React.useEffect(() => {
    if (dirty) {
      // sumbit on change
      handleSubmit()
    }
  }, [values])

  React.useEffect(() => {
    // use debounce to limit the number of submit
    setFieldValue('nomContact', name)
  }, [name])

  React.useEffect(() => {
    // use debounce to limit the number of submit
    setFieldValue('prenomContact', firstname)
  }, [firstname])

  React.useEffect(() => {
    // use debounce to limit the number of submit
    setFieldValue('emailContact', email)
  }, [email])

  React.useEffect(() => {
    // use debounce to limit the number of submit
    setFieldValue('telephoneContact', phone)
  }, [phone])

  React.useEffect(() => {
    // override with personal data
    if (form.user === 'me' && user) {
      onForm({
        nomContact: user.nom,
        prenomContact: user.prenom,
        emailContact: user.email,
        telephoneContact: user.numeroTelephonePortable || '',
      })
      setName(user.nom)
      setFirstName(user.prenom)
      setEmail(user.email)
      setPhone(user.numeroTelephonePortable || '')
    } else if (formHasUser(form)) {
      onForm({
        nomContact: form.nomContact,
        prenomContact: form.prenomContact,
        emailContact: form.emailContact,
        telephoneContact: form.telephoneContact,
      })
      setName(form.nomContact)
      setFirstName(form.prenomContact)
      setEmail(form.emailContact)
      setPhone(form.telephoneContact)
    } else {
      onForm({ nomContact: '', prenomContact: '', emailContact: '', telephoneContact: '' })
      setName('')
      setFirstName('')
      setEmail('')
      setPhone('')
    }
  }, [form.user])

  return (
    <MainContainer>
      <Title>{i18n.t('screens.incident.USER_DETAIL.firstname')}</Title>
      <TextInput
        value={currentFirstName}
        onChange={setFirstName}
        placeholder={i18n.t('screens.incident.USER_DETAIL.holder.firstname')}
        error={touched.prenomContact && errors.prenomContact ? i18n.t(errors.prenomContact) : ''}
      />

      <Title>{i18n.t('screens.incident.USER_DETAIL.name')}</Title>
      <TextInput
        value={currentName}
        onChange={setName}
        placeholder={i18n.t('screens.incident.USER_DETAIL.holder.name')}
        error={touched.nomContact && errors.nomContact ? i18n.t(errors.nomContact) : ''}
      />

      <Title>{i18n.t('screens.incident.USER_DETAIL.email')}</Title>
      <TextInput
        value={currentEmail}
        onChange={setEmail}
        placeholder={i18n.t('screens.incident.USER_DETAIL.holder.email')}
        error={touched.emailContact && errors.emailContact ? i18n.t(errors.emailContact) : ''}
      />

      <Title>{i18n.t('screens.incident.USER_DETAIL.phone')}</Title>
      <TextInput
        value={currentPhone}
        onChange={setPhone}
        placeholder={i18n.t('screens.incident.USER_DETAIL.holder.phone')}
        error={touched.telephoneContact && errors.telephoneContact ? i18n.t(errors.telephoneContact) : ''}
      />
    </MainContainer>
  )
}

export default UserDetail

const MainContainer = styled(View)`
  margin: 0 20px;
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? 0 : 75)}px 0;
`

const Title = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
  margin-bottom: 5px;
  margin-top: 25px;
`
